/**
 * Typed API client for the Agent Domain.
 */

import {
  request as apiRequest,
  authHeaders,
  handleUnauthorized,
  ApiError,
} from "./building";
import type { PerspectiveId } from "@/lib/perspectives";

// Re-exported so existing `import { ApiError } from "@/lib/api/agent"` callers keep working after
// the class moved to building.ts (the single request/401 seam).
export { ApiError } from "./building";

const BASE = "/api/agent";

// Bind the shared request implementation to the agent domain's base. All 401/authHeaders/ApiError
// behaviour lives once in building.ts; this only supplies the base.
const request = <T>(path: string, init?: RequestInit): Promise<T> =>
  apiRequest<T>(path, init, BASE);

export interface AgentDefinition {
  id: string;
  name: string;
  description: string;
  display_name: string;
  description_fi: string;
  capability_labels: string[];
  icon_type: "energy" | "analysis" | "strategy" | "config" | "simulation";
  schedule_minutes: number;
  is_active: boolean;
  last_run_at: string | null;
  // Persisted per-(agent, building) schedule enablement (story #1604) — null when `listAgents`
  // was called without `buildingId`, so a caller can never mistake "not asked" for "not paused".
  enabled_for_building: boolean | null;
}

export interface AgentScheduleEnablement {
  agent_id: string;
  building_id: string;
  enabled: boolean;
}

export interface AgentRun {
  id: string;
  agent_id: string;
  triggered_by: "scheduled" | "manual" | "event";
  triggered_by_user: string | null;
  building_ids: string[];
  reasoning_chain: string[];
  output_recipe_ids: string[];
  status: "running" | "completed" | "failed" | "timeout";
  duration_ms: number;
  error_detail: string | null;
  created_at: string;
}

export interface ChatSession {
  id: string;
  user_id: string;
  scope: ChatScope | null;
  messages: ChatMessage[];
  created_at: string;
  updated_at: string;
}

// ---------------------------------------------------------------------------
// Chat scope — which level the user is operating at
// ---------------------------------------------------------------------------

// `level: "inventory"` means the whole building inventory (Kiinteistökanta) — the
// canonical wire value on both ends (agent chat.py
// Literal["inventory","group","building"]). See docs/spec/terminology.md.
// `lens` carries the active perspective (Kysy agentilta) so the agent can resolve a
// lens agent profile (persona + tool allowlist + graph) server-side — see
// docs/spec/concepts.md#agent-chat and docs/reference/agent-system.md. Absent = the
// sidebar/general entry, unchanged.
export type ChatScope =
  | { level: "inventory"; lens?: PerspectiveId }
  | { level: "group"; context_id: string; lens?: PerspectiveId }
  | { level: "building"; context_id: string; lens?: PerspectiveId };

// ---------------------------------------------------------------------------
// Widget specs — graphical responses rendered inline in chat messages
// ---------------------------------------------------------------------------

export type ConfigActionKind =
  | "add_zone_link"
  | "remove_zone_link"
  | "add_dependency"
  | "remove_dependency"
  | "set_variants";

export type WidgetSpec =
  | {
      type: "render_trend_chart";
      building_id: string;
      channels: string[];
      days: number;
      title?: string;
    }
  | {
      type: "render_building_comparison";
      metric: string;
      building_ids: string[];
      days?: number;
    }
  | {
      type: "render_kpi_summary";
      title: string;
      items: Array<{
        label: string;
        value: number;
        unit: string;
        trend?: number;
      }>;
    }
  | {
      type: "render_multi_building_trend";
      building_ids: string[];
      metric: string;
      days: number;
      title?: string;
    }
  | {
      type: "render_config_action";
      kind: ConfigActionKind;
      rationale_fi: string;
      equipment_id?: string;
      equipment_name?: string;
      zone_id?: string;
      zone_name?: string;
      source_equipment_id?: string;
      source_equipment_name?: string;
      target_equipment_id?: string;
      target_equipment_name?: string;
      dependency_type?: "feeds";
      // set_variants: the equipment's full new declared-variant-choice id set. active_behaviors
      // is re-derived from these on the next read (post-#190 variant-derived model).
      declared_variants?: string[];
    };

export interface ChatMessage {
  id: string;
  session_id: string;
  role: "user" | "agent";
  content: string;
  building_refs: string[];
  created_at: string;
}

// Agents
export const listAgents = (buildingId?: string) =>
  request<AgentDefinition[]>(
    buildingId
      ? `/agents?building_id=${encodeURIComponent(buildingId)}`
      : "/agents",
  );
export const setAgentScheduleEnablement = (
  agentId: string,
  buildingId: string,
  enabled: boolean,
) =>
  request<AgentScheduleEnablement>(`/agents/${agentId}/schedule-enablement`, {
    method: "PUT",
    body: JSON.stringify({ building_id: buildingId, enabled }),
  });
export const triggerAgent = (agentId: string, buildingId: string) =>
  request<AgentRun>(`/agents/${agentId}/run`, {
    method: "POST",
    body: JSON.stringify({ building_id: buildingId }),
  });
export const listRuns = (agentId: string) =>
  request<AgentRun[]>(`/agents/${agentId}/runs`);
export const getReasoning = (runId: string) =>
  request<{ run_id: string; reasoning_chain: string[] }>(
    `/agents/runs/${runId}/reasoning`,
  );

// Chat sessions
export const createSession = (scope?: ChatScope, signal?: AbortSignal) =>
  request<ChatSession>("/chat/sessions", {
    method: "POST",
    body: JSON.stringify({ scope: scope ?? null }),
    signal,
  });

export const listSessions = () => request<ChatSession[]>("/chat/sessions");

export const getSession = (sessionId: string) =>
  request<ChatSession>(`/chat/sessions/${sessionId}`);

export const deleteSession = async (sessionId: string): Promise<void> => {
  const res = await fetch(`${BASE}/chat/sessions/${sessionId}`, {
    method: "DELETE",
    headers: authHeaders() as Record<string, string>,
  });
  // A 5xx must not read as success — the UI would drop a session that still exists.
  if (!res.ok) throw new Error(`Failed to delete session (${res.status})`);
};

// Weather context
export interface WeatherPoint {
  hour: string;
  temperature_c: number;
  solar_wm2: number;
}

export interface WeatherContext {
  points: WeatherPoint[];
  source: string;
}

export const fetchWeatherContext = (
  buildingId: string,
): Promise<WeatherContext> =>
  request<WeatherContext>("/agents/weather-context", {
    method: "POST",
    body: JSON.stringify({ building_id: buildingId }),
  });

export interface SuggestFollowupsResult {
  suggestions: string[];
}

export const suggestFollowups = (
  messages: { role: string; content: string }[],
  scope: ChatScope,
): Promise<SuggestFollowupsResult> =>
  request<SuggestFollowupsResult>("/agents/suggest-followups", {
    method: "POST",
    body: JSON.stringify({ messages, scope }),
  });

/** Minimal shape of `ReadableStream.getReader()` — the part `readSSEStream` needs. */
interface ByteStreamReader {
  read(): Promise<{ done: boolean; value?: Uint8Array }>;
}

/**
 * Consume an SSE byte stream, invoking `onDelta` per text delta and `onWidget` per widget,
 * and returning the concatenated text.
 *
 * A `data:` line can be split across two network reads. This buffers the trailing partial line
 * across reads and only parses complete (newline-terminated) lines, so a split line is never fed
 * to `JSON.parse` as two broken fragments and lost (bug #191). `{ stream: true }` on the decoder
 * fixes UTF-8 byte boundaries; this buffer fixes logical line boundaries.
 */
export async function readSSEStream(
  reader: ByteStreamReader,
  onDelta: (delta: string) => void,
  onWidget?: (widget: WidgetSpec) => void,
  onStatus?: (status: string) => void,
): Promise<string> {
  const decoder = new TextDecoder();
  let fullText = "";
  let buffer = "";

  // Returns true when the terminating `[DONE]` sentinel is seen.
  const handleLine = (line: string): boolean => {
    if (!line.startsWith("data: ")) return false;
    const payload = line.slice(6).trim();
    if (payload === "[DONE]") return true;
    let parsed: {
      delta?: string;
      widget?: WidgetSpec;
      error?: string;
      status?: string;
    };
    try {
      parsed = JSON.parse(payload) as typeof parsed;
    } catch {
      return false;
    }
    if (parsed.error) {
      throw new Error(parsed.error);
    }
    if (parsed.status && onStatus) {
      onStatus(parsed.status);
    }
    if (parsed.delta) {
      fullText += parsed.delta;
      onDelta(parsed.delta);
    }
    if (parsed.widget && onWidget) {
      onWidget(parsed.widget);
    }
    return false;
  };

  while (true) {
    const { done, value } = await reader.read();
    if (done) break;
    buffer += decoder.decode(value, { stream: true });
    const lines = buffer.split("\n");
    // The last element has no trailing newline yet — retain it for the next read.
    buffer = lines.pop() ?? "";
    for (const line of lines) {
      if (handleLine(line)) return fullText; // [DONE] terminates the stream
    }
  }

  // Stream closed — flush any complete line still sitting in the buffer.
  const tail = buffer + decoder.decode();
  if (tail) handleLine(tail);
  return fullText;
}

/**
 * Send a message and receive a streaming SSE response.
 * onDelta is called with each text chunk.
 * onWidget is called when the AI requests an inline graphical widget.
 * Returns the full text response.
 */
export async function sendMessage(
  sessionId: string,
  content: string,
  onDelta: (delta: string) => void,
  onWidget?: (widget: WidgetSpec) => void,
  signal?: AbortSignal,
  onStatus?: (status: string) => void,
): Promise<string> {
  const res = await fetch(`${BASE}/chat/sessions/${sessionId}/messages`, {
    method: "POST",
    headers: {
      "Content-Type": "application/json",
      ...(authHeaders() as Record<string, string>),
    },
    body: JSON.stringify({ content }),
    signal,
  });

  if (!res.ok || !res.body) {
    if (res.status === 401) {
      // The streaming path can't route through `request`, so it calls the same shared handler.
      handleUnauthorized();
      throw new ApiError("Unauthorized", 401);
    }
    throw new Error(`HTTP ${res.status}`);
  }

  return readSSEStream(res.body.getReader(), onDelta, onWidget, onStatus);
}

// ── KB behaviors ─────────────────────────────────────────────────────────────

export interface DataPointWireStatus {
  id: string;
  wired: boolean;
  bms_name?: string;
}

export type KBBehaviorStatus =
  | "active"
  | "fail"
  | "no_data"
  | "unready"
  | "unwired";

export type ParameterSource = "learned" | "config";

// One criterion of a behaviour and whether it can currently evaluate. `blocked_by` names the
// specific parameter ids missing — what makes the read-out actionable rather than merely red.
export interface CriterionReadiness {
  id: string;
  ready: boolean;
  blocked_by: string[];
}

// One parameter a behaviour's criteria depend on, learned or config alike.
// `samples` / `samples_required` / `last_learned_at` are learned-only and null for a config
// parameter — the null-ness is the signal that this one waits on a human, not on time.
export interface ParameterReadiness {
  id: string;
  name_fi: string;
  name_en: string;
  source: ParameterSource;
  state: string;
  value: number | null;
  unit: string | null;
  samples: number | null;
  samples_required: number | null;
  last_learned_at: string | null; // ISO datetime
}

// Composite zone capability status for one zone criterion (story #497). `affects` is the KB
// domain tag (`comfort`/`air_quality`/`energy`/`safety`), null for a criterion that declares
// none. `no_equipment` on `equipment_behavior`/`combined` is the honest "no equipment link for
// this domain" — never render it as a false active. `null` on an equipment behaviour (only zone
// criteria carry `affects`).
export interface ZoneCapability {
  affects: "comfort" | "air_quality" | "energy" | "safety" | null;
  zone_signal: "active" | "no_data" | "unready";
  equipment_behavior: "active" | "fail" | "no_data" | "no_equipment";
  combined: "active" | "fail" | "no_data" | "no_equipment" | "unready";
}

export interface KBBehavior {
  id: string;
  name_fi: string;
  description_fi: string;
  status: KBBehaviorStatus;
  required_data_points: DataPointWireStatus[];
  full_potential_weight: number;
  // Additive readiness detail (#66). `unready_reason` says why an unready behaviour is blocked;
  // `partially_ready` flags a behaviour whose coarse `status` hides some blocked criteria.
  unready_reason: "learning" | "config" | null;
  partially_ready: boolean;
  criteria: CriterionReadiness[];
  parameters: ParameterReadiness[];
  // Additive: populated for zone criteria only (story #497) — null for equipment behaviours.
  capability: ZoneCapability | null;
}

export interface EquipmentKBBehaviorsResponse {
  equipment_id: string;
  equipment_type: string;
  behaviors: KBBehavior[];
}

export const getKBBehaviors = (buildingId: string, equipmentId: string) =>
  request<EquipmentKBBehaviorsResponse>(
    `/buildings/${buildingId}/equipment/${equipmentId}/kb-behaviors`,
  );

export interface EquipmentBehaviorSummary {
  equipment_id: string;
  has_kb: boolean;
  active_count: number;
  fail_count: number;
  unwired_count: number;
  // Additive readiness counts from agent summary (defaults if older payloads omit them).
  unready_count?: number;
  partially_ready_count?: number;
  learning_count?: number;
}

export const getEquipmentKBBehaviorsSummary = (buildingId: string) =>
  request<EquipmentBehaviorSummary[]>(
    `/buildings/${buildingId}/equipment/kb-behaviors-summary`,
  );

// A zone's occupancy read-out (story #496). `source` is `measured` when a dedicated occupancy
// sensor is wired, `inferred` when resolved from the serving equipment's occupancy matrix +
// proxies. `confidence` is populated only for an inferred value. `ready` is false on cold-start
// (the serving matrix is not yet available), with `value` withheld — never a silent occupied.
//
// Kept (task #1766 removed only getZoneOccupancySummary/ZoneOccupancySummary and ZoneGrid's own
// occupancy-map path, both superseded by the zone-KPI contract — @/lib/zone-kpi.ts):
// `getZoneKBBehaviors`'s `ZoneKBBehaviorsResponse.occupancy` below still returns this shape, and
// Konfiguraatio's capability badge (components/configuration/{occupancy-badge,KBBehaviorList,
// CapabilityList.data}.ts) still reads it — out of task #1766's scope:frontend `extend:` list,
// so left as its own, still-valid read path rather than folded into zone-kpi.ts.
export interface OccupancyReadOut {
  source: "measured" | "inferred";
  ready: boolean;
  value: number | null;
  confidence: number | null;
}

// One equipment behaviour that wiring a zone signal would unlock, enriched server-side with the
// KB Finnish names the UI displays. `description_fi` already has the description_en fallback
// applied — render it verbatim (KB-first: the hint text lives in the KB, never hardcoded here).
export interface ZoneEnableHint {
  behavior_id: string;
  behavior_name_fi: string;
  equipment_type: string;
  equipment_name_fi: string;
  description_fi: string;
}

// A zone data point: whether it is wired on this zone, and what wiring it would unlock. Signal-
// centric — covers every criticality, so a `recommended` signal's unlock hint (co2 → co2_boost)
// surfaces even though it is absent from any behaviour's required_data_points.
export interface ZoneSignal {
  id: string;
  name_fi: string;
  criticality: string;
  wired: boolean;
  enables: ZoneEnableHint[];
}

export interface ZoneKBBehaviorsResponse {
  zone_id: string;
  zone_type: string;
  behaviors: KBBehavior[];
  // Additive: null when the zone type declares no occupancy inference.
  occupancy: OccupancyReadOut | null;
  // Additive: per-signal wiring + unlock hints. Decoupled from `behaviors[]`.
  signals: ZoneSignal[];
}

export const getZoneKBBehaviors = (buildingId: string, zoneId: string) =>
  request<ZoneKBBehaviorsResponse>(
    `/buildings/${buildingId}/zones/${zoneId}/kb-behaviors`,
  );

// One resolved zone KPI — käyttöaste, sisäilman laatu, or lämpötila (story #1758, task #1761):
// step, raw value, provenance rung and confidence, or an explicit not-ready. Mirrors the
// backend's own `ZoneKpiOut` contract exactly (services/agent/.../routes/behaviors.py).
// `ready=false` withholds every other field — never a zero, a low step, or a silent default.
export type ZoneKpiProvenance =
  | "native"
  | "equipment-published"
  | "substituted"
  | "inferred";

export interface ZoneKpi {
  ready: boolean;
  score: number | null;
  step: number | null;
  raw_value: number | null;
  provenance: ZoneKpiProvenance | null;
  confidence: number | null;
}

// The three zone KPIs together — käyttöaste, sisäilman laatu, lämpötila.
export interface ZoneKpis {
  occupancy: ZoneKpi;
  air_quality: ZoneKpi;
  temperature: ZoneKpi;
}

export interface ZoneKpisSummaryEntry {
  zone_id: string;
  kpis: ZoneKpis;
}

// One batch call for every zone's resolved KPI triple (task #1761) — the one shared contract
// Signaalit, the Sisäilmaolosuhteet zone card, and Energiatehokkuus's Sisäiset vaikuttimet chart
// all read (task #1766: @/lib/zone-kpi.ts), replacing three independently derived
// native/equipment-published/substituted/inferred answers with one already-resolved source of
// truth. Superseded the older getZoneOccupancySummary (occupancy only, no air_quality/
// temperature, no step/provenance) — a zone grid calls this once instead of getZoneKBBehaviors
// per zone.
export const getZoneKpisSummary = (buildingId: string) =>
  request<ZoneKpisSummaryEntry[]>(
    `/buildings/${buildingId}/zones/kpis-summary`,
  );

// A zone's compare-type equipment signals resolved against live readings, with provenance
// (story #493). `provenance` is "substituted" only when the substitute one-zone gate is met —
// otherwise a substitute-relation entry still surfaces, degraded to "equipment-published".
export type ZoneSignalProvenance = "equipment-published" | "substituted";

export interface ZoneEquipmentSignal {
  equipment_id: string;
  equipment_type: string;
  dependency_type: string;
  zone_receives_as: string;
  relation: "compare" | "substitute";
  provenance: ZoneSignalProvenance;
  description_fi: string;
  description_en: string;
  equipment_value: number | null;
  equipment_recorded_at: string | null; // ISO datetime
  zone_data_point_id: string | null;
  zone_value: number | null;
  zone_recorded_at: string | null; // ISO datetime
  delta: number | null;
}

export interface ZoneEquipmentSignalsResponse {
  zone_id: string;
  zone_type: string;
  signals: ZoneEquipmentSignal[];
}

export const getZoneEquipmentSignals = (buildingId: string, zoneId: string) =>
  request<ZoneEquipmentSignalsResponse>(
    `/buildings/${buildingId}/zones/${zoneId}/equipment-signals`,
  );

// getZoneEquipmentSignals for every zone in a building in one request — the batch counterpart
// a zone grid uses instead of calling getZoneEquipmentSignals once per visible zone.
export const getZoneEquipmentSignalsSummary = (buildingId: string) =>
  request<ZoneEquipmentSignalsResponse[]>(
    `/buildings/${buildingId}/zones/equipment-signals-summary`,
  );

// When the KB evaluation tick last swept this building. Null timestamps = never evaluated (no
// marker row) — the honest "not yet evaluated" state the building Analyysi timeline renders,
// distinct from "evaluated, clean". See docs/spec/knowledge-base.md § Evaluation-run marker.
export interface KBEvaluationRun {
  building_id: string;
  first_evaluated_at: string | null; // ISO datetime
  last_evaluated_at: string | null; // ISO datetime
}

export const getKBEvaluationRun = (buildingId: string) =>
  request<KBEvaluationRun>(`/buildings/${buildingId}/kb-evaluation-run`);

// Per-successful-sweep KB tick summaries — the auditable history behind the Analyysi timeline.
// Additive alongside the evaluation-run marker above: the marker owns evaluated-vs-not colouring,
// these let an operator open one recorded 15-min sweep and see what it did. See
// docs/spec/knowledge-base.md § KB tick summary.
export interface KBTickEquipmentResult {
  equipment_id: string;
  equipment_type: string;
  rules_evaluated: number;
  rules_failed: number;
  data_quality_findings: number;
}

// Headline fields of one recorded sweep — enough to render the detail dialog from list data.
export interface KBTickSummary {
  tick_id: string;
  started_at: string; // ISO datetime
  finished_at: string; // ISO datetime
  equipment_evaluated: number;
  zones_evaluated: number;
  data_points_written: number;
  behaviours_failing: number;
  errors: number;
}

export interface KBTickSummaryList {
  building_id: string;
  ticks: KBTickSummary[];
}

// Detail adds the per-equipment breakdown to the headline fields.
export interface KBTickSummaryDetail extends KBTickSummary {
  building_id: string;
  equipment: KBTickEquipmentResult[];
}

// Defaults to the last 24 h (the Analyysi strip's span) when from/to are omitted.
export const listKBTickSummaries = (buildingId: string) =>
  request<KBTickSummaryList>(`/buildings/${buildingId}/kb-tick-summaries`);

export const getKBTickSummary = (buildingId: string, tickId: string) =>
  request<KBTickSummaryDetail>(
    `/buildings/${buildingId}/kb-tick-summaries/${tickId}`,
  );

export const getBehaviorLabels = () =>
  request<Record<string, string>>("/behaviors/labels");

export const getKBEquipmentTypes = () =>
  request<string[]>("/behaviors/equipment-types");

let _kbTypesCache: Set<string> | null = null;
let _kbTypesFetch: Promise<Set<string>> | null = null;

export function getKBEquipmentTypesSet(): Promise<Set<string>> {
  if (_kbTypesCache) return Promise.resolve(_kbTypesCache);
  if (!_kbTypesFetch) {
    _kbTypesFetch = getKBEquipmentTypes().then((types) => {
      _kbTypesCache = new Set(types);
      return _kbTypesCache;
    });
  }
  return _kbTypesFetch;
}

// ── KB browser ────────────────────────────────────────────────────────────────

export interface KBDataPoint {
  id: string;
  name_fi: string;
  unit: string;
  criticality: "required" | "recommended" | "optional";
  kind?: string;
  order?: number | null;
  connection_source?: string | null;
  /** Sensor KB type this point binds for quality checks (`sensor_type` in YAML). */
  sensor_type?: string | null;
}

// A physical hardware sub-type (e.g. rotary vs. plate heat recovery) — `component.variants`
// in the KB YAML. Not the equipment-level Variant contract (KBConfigOptionChoice, below);
// see docs/spec/knowledge-base.md § Variants file.
export interface KBComponentSubtype {
  id: string;
  name_en: string;
  name_fi: string;
  notes: string;
}

export interface KBQuantityFamilyEstimator {
  id: string;
  kind: "measured" | "derived" | "accepted";
  data_point_id: string;
  name_fi?: string;
  name_en?: string;
  unit?: string;
  expression?: string;
  requires_config?: string[];
  requires_learned?: string[];
}

export interface KBQuantityFamily {
  id: string;
  name_fi?: string;
  name_en?: string;
  unit?: string;
  priority: string[];
  estimators: KBQuantityFamilyEstimator[];
}

export interface KBComponent {
  id: string;
  name_fi: string;
  description_fi: string;
  data_points: KBDataPoint[];
  full_potential_weight: number;
  optional: boolean;
  variants: KBComponentSubtype[];
  // A component-owned criterion (story #690's Signaalisuhde migration onto components) — a
  // first-class Signaalisuhde of this component's Komponentti dossier, and also aggregated
  // onto every Virta whose chain[].component names this component (dual-lens, #748).
  operational_criteria: KBOperationalCriterion[];
  /** Quantity families this component authors (computed signals). */
  quantity_families?: KBQuantityFamily[];
}

/** One published source behind a cited claim, at the place the claim was read. */
export interface KBCitedSource {
  ref: string;
  title: string;
  edition: string;
  locator: string;
}

// Where a rule comes from (REQ-AGT-28). The agent resolves the criterion's `claims:` against the
// source library when it answers, so the standard's name lives in one place only — never copied
// into the criterion's Finnish text, where it would go stale on a new edition.
export interface KBClaimCitation {
  ref: string;
  /** limit | scope | point | exception | persistence | cause — what the claim does for this rule. */
  role: string;
  /** `verified`, `pending`, or `unresolved` when the ref names no claim. */
  verification: string;
  statement_en: string;
  measuring_point: string | null;
  sources: KBCitedSource[];
}

export interface KBOperationalCriterion {
  id: string;
  name_fi: string;
  name_en: string;
  severity: string;
  /** Empty when the criterion cites nothing — show no source rather than inventing one. */
  claims?: KBClaimCitation[];
  // Bare data_point ids the criterion's expression reads (story #1235/#1237) — resolved against
  // an analysis chain's step data_point to decide which Virta(s) this capability signals (no
  // hardcoded frontend criterion-id map). Absent/empty means the source expression wasn't
  // available to derive signals from.
  required_data_point_ids?: string[];
}

// A Mittaukset entry on a capability card — the signal a capability requires, plus a resolvable
// source label (component · laitteen taso · zone/peer boundary) when the KB author declared one.
export interface KBRequiredDataPoint {
  id: string;
  source: string | null;
}

export interface KBBehaviorDef {
  id: string;
  name_fi: string;
  description_fi: string;
  full_potential_weight: number;
  operational_criteria: KBOperationalCriterion[];
  // Bare data_point ids — resolved against a component's data_points or an analysis chain's
  // data_point to derive this behavior's Virta/Komponentti placement (no hardcoded frontend map).
  required_data_point_ids: string[];
  // Mittaukset for the capability card contract (docs/spec/knowledge-base.md § Capability
  // card contract) — required_data_point_ids paired with an optional source label.
  required_data_points: KBRequiredDataPoint[];
  /** Tietopohja layer chip — seuranta / potentiaali / optimointi. */
  layer?: "monitoring" | "potential" | "optimization";
  /** Tietopohja home — komponentti / polku / laite / riippuvuus. */
  home?: "component" | "path" | "unit" | "dependency";
  /** Parent component ids or analysis (Virta) ids when home is component/path. */
  under_ids?: string[];
  /** Paired sibling behavior id (e.g. supply/extract filter). */
  pair_id?: string | null;
}

export interface KBAnalysisChainStep {
  component: string;
  data_point: string;
  label_fi: string;
  label_en: string;
  side: string;
}

export interface KBAnalysis {
  id: string;
  name_fi: string;
  name_en: string;
  description_fi: string;
  chain: KBAnalysisChainStep[];
  operational_criteria: KBOperationalCriterion[];
  runtime_status: "evaluated" | "loaded";
}

export interface KBConnectionAnalysis {
  id: string;
  name_fi: string;
  name_en: string;
  description_fi: string;
  description_en: string;
  source: "equipment_connection" | "zone_connection";
  dependency_type: string;
  counterpart_type: string;
  operational_criteria?: KBOperationalCriterion[];
}

export interface KBBoundarySignal {
  id: string;
  name_fi: string;
  side: "self" | "peer";
  unit: string;
}

/** Riippuvuudet dossier projected from a connection YAML file. */
export interface KBDependencyGroup {
  id: string;
  counterpart_type: string;
  dependency_type: string;
  source: "equipment_connection" | "zone_connection";
  name_fi: string;
  name_en: string;
  description_fi: string;
  description_en: string;
  boundary_signals: KBBoundarySignal[];
  analysis_ids: string[];
}

export interface KBConfigOptionChoice {
  id: string;
  name_fi: string;
  description_fi: string;
  enables_behaviors: string[];
  disables_behaviors: string[];
  // Component-gating pair, parallel to enables_behaviors/disables_behaviors — component
  // ids of this equipment type gated by declaring this choice.
  enables_components: string[];
  disables_components: string[];
}

export interface KBConfigOptionGroup {
  id: string;
  name_fi: string;
  description_fi: string;
  type: "enum" | "boolean";
  choices: KBConfigOptionChoice[];
  enables_behaviors: string[];
  disables_behaviors: string[];
  enables_components: string[];
  disables_components: string[];
}

export interface KBEquipmentSummary {
  equipment_type: string;
  name_fi: string;
  name_en: string;
  icon: string;
  category_fi: string;
}

// One `equipments/<type>/streams.yaml` duct/pipe grouping (docs/spec/knowledge-base.md
// § Stream catalog — grouping components by duct and pipe). `components` is the ordered
// membership list; the same component id may appear on more than one stream (e.g. a
// heat-recovery element on both `supply_air` and `extract_air`).
export interface KBStream {
  id: string;
  order: number;
  name_fi: string;
  name_en: string;
  medium: string;
  components: string[];
}

export interface KBEquipmentType {
  equipment_type: string;
  name_fi: string;
  name_en: string;
  description_fi: string;
  description_en: string;
  icon: string;
  category_fi: string;
  components: KBComponent[];
  shared_data_points?: KBDataPoint[];
  behaviors: KBBehaviorDef[];
  config_option_groups: KBConfigOptionGroup[];
  total_weight: number;
  analyses: KBAnalysis[];
  connection_analyses: KBConnectionAnalysis[];
  // Optional duct/pipe catalog from equipments/<type>/streams.yaml — [] for a type that
  // declares no file (docs/spec/knowledge-base.md § Stream catalog).
  streams?: KBStream[];
  dependency_groups?: KBDependencyGroup[];
}

export interface KBService {
  id: string;
  name_fi: string;
  description_fi: string;
  description_en: string;
  compatible_equipment: string[];
}

export interface KBZoneDataPoint {
  id: string;
  name_fi: string;
  unit: string;
  criticality: "required" | "recommended" | "optional";
  full_potential_weight: number;
  // What wiring this signal would unlock — definition view, no per-instance wired status.
  enables: ZoneEnableHint[];
}

export interface KBZonePublishedSignal {
  equipment_data_point: string;
  zone_receives_as: string;
  relation: "compare" | "substitute";
  compares_to_zone_data_point: string | null;
  description_fi: string;
  description_en: string;
  optional: boolean;
}

export interface KBZoneConnectableEquipment {
  equipment_type: string;
  dependency_type: string;
  equipment_publishes: KBZonePublishedSignal[];
}

/** Declared occupancy KPI table — mapping endpoints live on occupancy_profile. */
export type KBZoneOccupancyKpi = Record<string, never>;

export interface KBZoneAirQualityKpi {
  poor_co2_ppm: number;
}

export interface KBZoneTemperatureKpi {
  sources: string[];
}

// Käyttöaste, sisäilman laatu, lämpötila — declared tables only. The on-screen indicator is
// the shared −100…+100 / 10-step score, not these objects.
export interface KBZoneKpiSteps {
  occupancy: KBZoneOccupancyKpi | null;
  air_quality: KBZoneAirQualityKpi | null;
  temperature: KBZoneTemperatureKpi | null;
}

export interface KBZoneType {
  zone_type: string;
  name_fi: string;
  description_fi: string;
  description_en: string;
  data_points: KBZoneDataPoint[];
  criteria_count: number;
  total_weight: number;
  connectable_equipment: KBZoneConnectableEquipment[];
  kpi_steps?: KBZoneKpiSteps;
}

export interface KBZoneSummary {
  id: string;
  name_fi: string;
  icon: string;
}

export interface KBSensorSummary {
  id: string;
  device_class: "meter" | "sensor" | "actuator" | "drive";
  name_fi: string;
  unit: string;
}

export interface KBQualityCheck {
  id: string;
  name_fi: string;
  severity: "fault" | "warning";
  algorithm_type: string;
  suppress_when_count: number;
  optional: boolean;
  description_fi?: string;
  description_en?: string;
  max_gap_minutes?: number | null;
}

export interface KBVariantChoice {
  id: string;
  name_fi: string;
  description_fi: string;
}

export interface KBSensorVariants {
  id: string;
  name_fi: string;
  choices: KBVariantChoice[];
}

export interface KBSensorType {
  device_type: string;
  device_class: "meter" | "sensor" | "actuator" | "drive";
  name_fi: string;
  description_en: string;
  description_fi: string;
  unit: string;
  bounds_min: number | null;
  bounds_max: number | null;
  quality_checks: KBQualityCheck[];
  variants: KBSensorVariants | null;
  /** Equipment data points that declare this sensor_type (quality-check binding). */
  bound_data_points?: KBDataPoint[];
}

export interface KBTypesResponse {
  equipment_types: KBEquipmentSummary[];
  zone_types: KBZoneSummary[];
  services: KBService[];
  sensor_types: KBSensorSummary[];
}

export type KBCriterionText = {
  observation_fi: string;
  // Selitys / Toimenpide (docs/spec/knowledge-base.md § Capability card contract).
  // `recommendation_fi`/`action_fi` are the pre-#787 legacy fields — kept for the findings
  // consumers that haven't migrated to remedy_fi yet (services/agent kb_advisory.py keeps
  // recommendation_fi as a compatibility alias of remedy_fi).
  explanation_fi: string | null;
  remedy_fi: string | null;
  recommendation_fi: string | null;
  action_fi: string | null;
};

export const getKBCriteriaText = () =>
  request<Record<string, KBCriterionText>>("/kb/criteria-text");

export const getKBDataPointLabels = () =>
  request<Record<string, string>>("/kb/data-point-labels");

export const getKBEquipmentTypeMeta = () =>
  request<Record<string, KBEquipmentSummary>>("/kb/equipment-type-meta");

export const getKBTypes = () => request<KBTypesResponse>("/kb/types");

export const getKBEquipmentType = (equipmentType: string) =>
  request<KBEquipmentType>(`/kb/equipment/${equipmentType}`);

export const getKBZoneType = (zoneType: string) =>
  request<KBZoneType>(`/kb/zones/${zoneType}`);

export const getKBSensorType = (sensorType: string) =>
  request<KBSensorType>(`/kb/sensors/${sensorType}`);

export interface BuildingCompletenessSummary {
  building_id: string;
  status: "configured" | "needs_attention";
  configured_items: number;
  total_items: number;
}

// Portfolio configuration-completeness rollup (one row per building). Sourced from the KB —
// see the agent completeness endpoints (story #174).
export const getCompletenessSummary = () =>
  request<BuildingCompletenessSummary[]>("/buildings/completeness-summary");

export interface ItemCompleteness {
  equipment_id: string;
  equipment_type: string;
  status: "configured" | "partial" | "needs_attention";
  // Criterion ids the item does not meet — `typed` · `variants` · `data_points` · `config` ·
  // `zone` · `dependencies`, in the backend's display order. Typed as plain strings, not a union:
  // the list is KB-derived and may grow before the frontend knows the new id, and a `Record` keyed
  // on a stale union yields `undefined` at runtime (see knowledge-base.md § Backwards
  // compatibility). Render via `criterionLabel()`, which falls back to the raw id.
  unmet: string[];
}

export interface BuildingCompleteness extends BuildingCompletenessSummary {
  zone_count: number;
  items: ItemCompleteness[];
}

// One building's configuration completeness, with the per-equipment `unmet` breakdown. The
// canonical, KB-derived signal — the same computation the portfolio rollup above serves.
export const getBuildingCompleteness = (buildingId: string) =>
  request<BuildingCompleteness>(`/buildings/${buildingId}/completeness`);

// Per-channel missing-reading gap window (minutes), sourced from each channel's KB sensor
// `max_gap_minutes` (story #246). Consumed by `chart-gaps.ts` for trend-chart staleness detection.
export const getGapWindows = () =>
  request<Record<string, number>>("/kb/gap-windows");
